/**
 * Client state (spec §3.5): identity, connection status, the current `PublicRoomView`, a feed of
 * game events/reactions for animations, and the server clock offset.
 *
 * Realtime wiring lives here too: `bindSocket` attaches the socket listeners once, and every
 * (re)connect automatically re-joins the current room with the stored identity.
 */
import { IntentSchema, type GameEvent, type Intent, type PublicRoomView } from '@20fight/engine';
import type { KickReason, Reaction, ReactionEmoji } from '@20fight/server/protocol';
import { create } from 'zustand';
import { tr } from '@/i18n';
import { isValidRoomCode, normalizeRoomCode } from '@/lib/room-code';
import type { AppError } from '@/net/errors';
import {
  emitWithAck,
  ensureConnected,
  getSocket,
  type ClientAck,
  type ClientAckError,
  type GameSocket,
} from '@/net/socket';
import { ClockOffset } from './clock';
import {
  clearLastRoom,
  getIdentity,
  getLastRoom,
  setLastRoom,
  updateProfile,
  type Identity,
} from './identity';

export type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'offline';
export type JoinState =
  'none' | 'joining' | 'pending-approval' | 'rejected' | 'joined' | 'other-tab';

export type FeedEntry =
  | { seq: number; kind: 'event'; event: GameEvent; at: number }
  | { seq: number; kind: 'reaction'; reaction: Reaction; at: number };

/** A failed join; `withPassword` tells a wrong password apart from a missing one. */
export interface JoinError extends AppError {
  withPassword: boolean;
}

export interface Toast {
  id: number;
  message: string;
  tone: 'info' | 'success' | 'error';
}

/** Max entries kept in the event/reaction feed. */
export const FEED_LIMIT = 100;
/** Automatic rejoin attempts per connection before giving up until the next reconnect. */
export const REJOIN_ATTEMPTS = 3;

export interface StoreState {
  identity: Identity;
  connection: ConnectionStatus;
  /** Room the client is in (or trying to join); drives rejoin-on-reconnect. */
  roomCode: string | null;
  room: PublicRoomView | null;
  joinState: JoinState;
  /** Why the last join attempt failed (e.g. `WRONG_PASSWORD` → show the password prompt). */
  joinError: JoinError | null;
  /** Last failed call of any kind. */
  lastError: AppError | null;
  /** Set when the server removed us from a room (kicked / banned / room closed). */
  kicked: { code: string; reason: KickReason } | null;
  /** Smoothed `serverNow - Date.now()` in ms. */
  clockOffset: number;
  feed: FeedEntry[];
  /** Seq of the newest feed entry (monotonic for the page lifetime). */
  lastSeq: number;
  toasts: Toast[];
  lastRoom: string | null;
  /** The server allows lobby bots (`ENABLE_BOTS`); learned from the create/join ack. */
  botsEnabled: boolean;
}

/** Ack of `room:create` / `room:join`. */
type RoomAck = { code: string; bots: boolean };

export interface StoreActions {
  setProfile(patch: { name?: string; avatar?: string }): Identity;
  /** Open the socket (no-op when already connected/connecting). */
  connect(): void;
  /** `name`: optional room name (3–6 of A–Z/0–9); a generated code otherwise. */
  /** `listed: false` keeps the room out of the home-page room list. */
  createRoom(name?: string, listed?: boolean): Promise<ClientAck<RoomAck>>;
  joinRoom(code: string, password?: string): Promise<ClientAck<RoomAck>>;
  /** Join `code` unless already joined / joining / waiting for it. */
  ensureJoined(code: string): void;
  leaveRoom(): Promise<void>;
  /** "Burada devam et": take the seat back after another tab replaced this one. */
  resumeHere(): Promise<void>;
  sendIntent(intent: Intent): Promise<ClientAck>;
  react(emoji: ReactionEmoji): Promise<ClientAck>;
  addBot(team?: 'A' | 'B' | 'jury'): Promise<ClientAck<{ playerId: string }>>;
  removeBot(targetId: string): Promise<ClientAck>;
  pushToast(message: string, tone?: Toast['tone']): void;
  dismissToast(id: number): void;
  clearError(): void;
  /** Forget the last kick (after the UI reacted to it). */
  clearKicked(): void;
}

export type Store = StoreState & StoreActions;

const clock = new ClockOffset();
/** Set by the store initializer below; attaches the realtime listeners to a socket. */
let bindSocketImpl: (socket: GameSocket) => () => void = () => () => {};
const boundSockets = new WeakSet<GameSocket>();
/** Detaches the listeners again (dev HMR: a re-evaluated store must not double-bind). */
let unbindSocket: (() => void) | null = null;
let toastSeq = 0;
/** Password of the current join (kept in memory only, reused for rejoin until joined). */
let joinPassword: string | undefined;
/** True while a join request of the user is in flight (suppresses the automatic rejoin). */
let joinInFlight = false;
/** Bumped per (re)connect so a superseded rejoin loop stops. */
let rejoinGeneration = 0;

const isTransportError = (code: string): boolean => code === 'TIMEOUT' || code === 'OFFLINE';

function initialState(): StoreState {
  return {
    identity: getIdentity(),
    connection: 'idle',
    roomCode: null,
    room: null,
    joinState: 'none',
    joinError: null,
    lastError: null,
    kicked: null,
    clockOffset: 0,
    feed: [],
    lastSeq: 0,
    toasts: [],
    lastRoom: getLastRoom(),
    botsEnabled: false,
  };
}

function toError(ack: ClientAckError): AppError {
  return ack.detail === undefined ? { code: ack.error } : { code: ack.error, detail: ack.detail };
}

const offline: ClientAckError = { ok: false, error: 'OFFLINE' };

export const useStore = create<Store>()((set, get) => {
  const fail = <T extends ClientAck<object>>(ack: T): T => {
    if (!ack.ok) set({ lastError: toError(ack) });
    return ack;
  };

  /** Shared result handling of `room:join` (user joins and automatic rejoins). */
  const applyJoinAck = (code: string, ack: ClientAck<RoomAck>, automatic: boolean): void => {
    if (get().roomCode !== code) return; // user moved on meanwhile
    if (ack.ok) {
      joinPassword = undefined;
      setLastRoom(ack.code);
      set({
        roomCode: ack.code,
        joinState: 'joined',
        joinError: null,
        kicked: null,
        lastRoom: ack.code,
        botsEnabled: ack.bots,
      });
      return;
    }
    if (ack.error === 'PENDING_APPROVAL') {
      set({ joinState: 'pending-approval', joinError: null });
      return;
    }
    // An automatic rejoin that failed on transport keeps its state: `rejoin` retries, or the next
    // reconnect does.
    if (automatic && isTransportError(ack.error)) return;
    if (ack.error === 'NOT_FOUND' || ack.error === 'BANNED') {
      if (getLastRoom() === code) clearLastRoom();
      set({ lastRoom: getLastRoom() });
    }
    set({
      joinState: 'none',
      joinError: { ...toError(ack), withPassword: joinPassword !== undefined },
      room: null,
    });
  };

  const sendJoin = async (code: string, automatic: boolean): Promise<ClientAck<RoomAck>> => {
    const payload: { code: string; profile: Identity; password?: string } = {
      code,
      profile: get().identity,
    };
    if (joinPassword !== undefined) payload.password = joinPassword;
    const ack = await emitWithAck('room:join', payload);
    applyJoinAck(code, ack, automatic);
    return ack;
  };

  /** Room whose seat we hold or are acquiring (and therefore want back after a reconnect). */
  const wantedRoom = (): string | null => {
    const { roomCode, joinState } = get();
    return roomCode !== null &&
      (joinState === 'joined' || joinState === 'pending-approval' || joinState === 'joining')
      ? roomCode
      : null;
  };

  /**
   * Called on every (re)connect: take the seat back in the current room. The connection stays
   * `reconnecting` until the seat is back so nothing sends intents too early (`requireSeat`).
   */
  const rejoin = async (): Promise<void> => {
    const generation = ++rejoinGeneration;
    const code = wantedRoom();
    if (code === null || joinInFlight) {
      set({ connection: 'connected' });
      return;
    }
    set({ connection: 'reconnecting' });
    for (let attempt = 1; attempt <= REJOIN_ATTEMPTS; attempt++) {
      const ack = await sendJoin(code, true);
      // Superseded by a newer connect (its own loop runs) or the transport dropped meanwhile.
      if (generation !== rejoinGeneration || !getSocket().connected) return;
      if (ack.ok || !isTransportError(ack.error) || wantedRoom() !== code) break;
    }
    set({ connection: 'connected' });
  };

  const pushFeed = (
    entry:
      | { kind: 'event'; event: GameEvent; at: number }
      | { kind: 'reaction'; reaction: Reaction; at: number },
  ): void => {
    const { feed, lastSeq } = get();
    const seq = lastSeq + 1;
    const next = [...feed, { ...entry, seq } as FeedEntry];
    if (next.length > FEED_LIMIT) next.splice(0, next.length - FEED_LIMIT);
    set({ feed: next, lastSeq: seq });
  };

  /** Whether a `state` for room `code` belongs to what we are doing right now. */
  const accepts = (code: string): boolean => {
    const { roomCode, joinState } = get();
    if (joinState === 'other-tab') return false;
    if (roomCode === code) return true;
    return roomCode === null && joinState === 'joining'; // room:create in flight
  };

  bindSocketImpl = (socket: GameSocket): (() => void) => {
    const onConnect = (): void => {
      void rejoin();
    };
    const onDisconnect = (reason: string): void => {
      if (get().joinState === 'other-tab' || reason === 'io client disconnect') {
        set({ connection: 'idle' });
      } else if (reason === 'io server disconnect') {
        set({ connection: 'offline' });
      } else {
        set({ connection: 'reconnecting' });
      }
    };
    const onConnectError = (): void => {
      const { connection } = get();
      if (!socket.active) set({ connection: 'offline' });
      else if (connection !== 'reconnecting') set({ connection: 'connecting' });
    };
    const onReconnectAttempt = (): void => {
      if (get().joinState !== 'other-tab') set({ connection: 'reconnecting' });
    };
    const onReconnectFailed = (): void => set({ connection: 'offline' });
    // The browser learns about a dropped network (Wi-Fi off, cable out) long before socket.io's
    // ping timeout (~45 s), during which the badge kept saying "Bağlı" and the view froze. Drop
    // the transport right away: the badge shows the reconnect, and the manager's retries (≤ 4 s
    // apart) take the seat back with a fresh state as soon as the network returns.
    const onBrowserOffline = (): void => {
      if (socket.connected) socket.io.engine?.close();
    };

    const onState = (view: PublicRoomView): void => {
      if (!accepts(view.code)) return;
      const offset = clock.sample(view.serverNow, Date.now());
      const { joinState } = get();
      const patch: Partial<StoreState> = { room: view, roomCode: view.code, clockOffset: offset };
      if (joinState === 'joining' || joinState === 'pending-approval') patch.joinState = 'joined';
      set(patch);
    };
    const onEvent = (event: GameEvent): void => {
      if (get().room === null) return;
      pushFeed({ kind: 'event', event, at: Date.now() });
    };
    const onReaction = (reaction: Reaction): void => {
      if (get().room === null) return;
      pushFeed({ kind: 'reaction', reaction, at: Date.now() });
    };
    const onKicked = ({ reason }: { reason: KickReason }): void => {
      const code = get().roomCode;
      if (reason === 'other-tab') {
        set({ joinState: 'other-tab', connection: 'idle' });
        return;
      }
      if (code && getLastRoom() === code) clearLastRoom();
      set({
        room: null,
        roomCode: null,
        joinState: 'none',
        joinError: null,
        kicked: code ? { code, reason } : null,
        lastRoom: getLastRoom(),
      });
      get().pushToast(tr.room.kicked[reason], 'error');
    };
    const onApproved = ({ code, bots }: { code: string; bots: boolean }): void => {
      if (get().roomCode !== code) return;
      joinPassword = undefined;
      setLastRoom(code);
      set({ joinState: 'joined', joinError: null, lastRoom: code, botsEnabled: bots });
    };
    const onRejected = ({ code }: { code: string }): void => {
      if (get().roomCode !== code) return;
      set({ joinState: 'rejected', room: null });
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    socket.io.on('reconnect_attempt', onReconnectAttempt);
    socket.io.on('reconnect_failed', onReconnectFailed);
    socket.on('state', onState);
    socket.on('event', onEvent);
    socket.on('reaction', onReaction);
    socket.on('kicked', onKicked);
    socket.on('join:approved', onApproved);
    socket.on('join:rejected', onRejected);
    if (typeof window !== 'undefined') window.addEventListener('offline', onBrowserOffline);

    return () => {
      if (typeof window !== 'undefined') window.removeEventListener('offline', onBrowserOffline);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
      socket.io.off('reconnect_attempt', onReconnectAttempt);
      socket.io.off('reconnect_failed', onReconnectFailed);
      socket.off('state', onState);
      socket.off('event', onEvent);
      socket.off('reaction', onReaction);
      socket.off('kicked', onKicked);
      socket.off('join:approved', onApproved);
      socket.off('join:rejected', onRejected);
      boundSockets.delete(socket);
    };
  };

  /**
   * Whether an intent may go out now: the socket is up *and* the seat is confirmed (`connection`
   * stays `reconnecting` while a rejoin is in flight, even though the socket is already connected).
   */
  const requireSeat = (): boolean => {
    const { connection, joinState } = get();
    return connection === 'connected' && joinState === 'joined' && getSocket().connected;
  };

  return {
    ...initialState(),

    setProfile(patch) {
      const identity = updateProfile(patch);
      set({ identity });
      return identity;
    },

    connect() {
      const socket = bindSocket();
      if (socket.connected) {
        // Socket already up but this store never saw it connect (dev HMR re-created the store).
        if (get().connection === 'idle') set({ connection: 'connected' });
        return;
      }
      if (socket.active) return;
      set({ connection: 'connecting' });
      socket.connect();
    },

    async createRoom(name, listed = true) {
      bindSocket();
      joinPassword = undefined;
      set({
        roomCode: null,
        room: null,
        joinState: 'joining',
        joinError: null,
        kicked: null,
        feed: [],
      });
      joinInFlight = true;
      try {
        get().connect();
        if (!(await ensureConnected())) {
          set({ joinState: 'none' });
          return fail({ ok: false, error: 'OFFLINE' } as ClientAck<RoomAck>);
        }
        const ack = await emitWithAck('room:create', {
          profile: get().identity,
          ...(name ? { code: name } : {}),
          listed,
        });
        if (ack.ok) {
          setLastRoom(ack.code);
          set({
            roomCode: ack.code,
            joinState: 'joined',
            lastRoom: ack.code,
            botsEnabled: ack.bots,
          });
        } else {
          set({ joinState: 'none' });
        }
        return fail(ack);
      } finally {
        joinInFlight = false;
      }
    },

    async joinRoom(rawCode, password) {
      bindSocket();
      const code = normalizeRoomCode(rawCode);
      const prev = get();
      joinPassword = password;
      set({
        roomCode: code,
        joinState: 'joining',
        joinError: null,
        kicked: null,
        ...(prev.roomCode !== code ? { room: null, feed: [] } : {}),
      });
      if (!isValidRoomCode(code)) {
        // Malformed code (e.g. a mistyped link): no such room can exist, no need to ask.
        const ack: ClientAck<RoomAck> = { ok: false, error: 'NOT_FOUND' };
        applyJoinAck(code, ack, false);
        return ack;
      }
      joinInFlight = true;
      try {
        get().connect();
        if (!(await ensureConnected())) {
          const ack: ClientAck<RoomAck> = { ok: false, error: 'OFFLINE' };
          applyJoinAck(code, ack, false);
          return ack;
        }
        return await sendJoin(code, false);
      } finally {
        joinInFlight = false;
      }
    },

    ensureJoined(rawCode) {
      const code = normalizeRoomCode(rawCode);
      const { roomCode, joinState } = get();
      if (
        roomCode === code &&
        (joinState === 'joined' ||
          joinState === 'joining' ||
          joinState === 'pending-approval' ||
          joinState === 'other-tab')
      ) {
        return;
      }
      void get().joinRoom(code);
    },

    async leaveRoom() {
      const { roomCode } = get();
      joinPassword = undefined;
      if (roomCode && getLastRoom() === roomCode) clearLastRoom();
      set({
        roomCode: null,
        room: null,
        joinState: 'none',
        joinError: null,
        feed: [],
        lastRoom: getLastRoom(),
      });
      if (getSocket().connected) await emitWithAck('room:leave', {});
    },

    async resumeHere() {
      const { roomCode } = get();
      if (!roomCode) return;
      set({ joinState: 'joining', joinError: null });
      // The server disconnected the replaced socket; the `connect` handler re-joins.
      get().connect();
      if (getSocket().connected) await rejoin();
    },

    async sendIntent(intent) {
      const parsed = IntentSchema.safeParse(intent);
      if (!parsed.success) {
        return fail({
          ok: false,
          error: 'INVALID_INTENT',
          detail: parsed.error.issues[0]?.message ?? 'invalid',
        } as ClientAck);
      }
      if (!requireSeat()) return fail(offline as ClientAck);
      return fail(await emitWithAck('intent', parsed.data));
    },

    async react(emoji) {
      if (!requireSeat()) return offline;
      // Reactions are fire-and-forget: a rate-limit ack is not worth an error banner.
      return emitWithAck('react', { emoji });
    },

    async addBot(team) {
      if (!requireSeat()) return fail(offline as ClientAck<{ playerId: string }>);
      return fail(await emitWithAck('bot:add', team ? { team } : {}));
    },

    async removeBot(targetId) {
      if (!requireSeat()) return fail(offline as ClientAck);
      return fail(await emitWithAck('bot:remove', { targetId }));
    },

    pushToast(message, tone = 'info') {
      const id = ++toastSeq;
      set({ toasts: [...get().toasts.slice(-4), { id, message, tone }] });
    },

    dismissToast(id) {
      set({ toasts: get().toasts.filter((t) => t.id !== id) });
    },

    clearError() {
      set({ lastError: null });
    },

    clearKicked() {
      set({ kicked: null });
    },
  };
});

/** Attach the store's listeners to the socket singleton (once per socket instance). */
export function bindSocket(): GameSocket {
  const socket = getSocket();
  if (!boundSockets.has(socket)) {
    boundSockets.add(socket);
    unbindSocket = bindSocketImpl(socket);
  }
  return socket;
}

// Dev HMR: detach this store's listeners before the re-evaluated module binds its own.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    unbindSocket?.();
    unbindSocket = null;
  });
}

/** Current server time estimate (epoch ms). */
export function serverNow(): number {
  return Date.now() + useStore.getState().clockOffset;
}

/** Test helper: reset the store, the clock and module flags. */
export function _resetStore(): void {
  clock.reset();
  joinPassword = undefined;
  joinInFlight = false;
  rejoinGeneration = 0;
  useStore.setState(initialState());
}
